import React from 'react';

import { amber, blue, green, grey } from '@mui/material/colors';

import { makeStyles, withStyles } from '@material-ui/core/styles';
import Avatar     from '@material-ui/core/Avatar';
import Badge      from '@material-ui/core/Badge';
import Button     from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';

import PlaylistAddCheckOutlinedIcon from '@material-ui/icons/PlaylistAddCheckOutlined';

import { create, date } from '../util';

const StyledBadge = withStyles((theme) => ({
  badge: {
    backgroundColor: green['A400'],
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      content: '""',
    },
  },
}))(Badge);

const recreate = {
  attendedToday: (lastAttended) => {
    if (lastAttended) {
      const now = new Date();
      const last = new Date(lastAttended);
      if (
        now.getFullYear() === last.getFullYear() &&
        now.getMonth() === last.getMonth() &&
        now.getDate() === last.getDate()
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  },
  attenders: (schedule) => {
    let newAttenders = [];
    for (const attending of schedule) {
      newAttenders.push([
        attending['attender.rowid'], attending['attender.name']
      ]);
    }
    return newAttenders;
  },
  timeAgo: (timestamp) => {
    if (timestamp) {
      let timestampAgo = Date.now() - timestamp;
      if (timestampAgo > 1000 * 60 * 60 * 24 * 7) {
        return `${Math.floor(timestampAgo / 1000 / 60 / 60 / 24 / 7)}주 전`;
      } else if (timestampAgo > 1000 * 60 * 60 * 24) {
        return `${Math.floor(timestampAgo / 1000 / 60 / 60 / 24)}일 전`
      } else if (timestampAgo > 1000 * 60 * 60) {
        return `${Math.floor(timestampAgo / 1000 / 60 / 60)}시간 전`
      } else if (timestampAgo > 1000 * 60) {
        return `${Math.floor(timestampAgo / 1000 / 60)}분 전`
      } else if (timestampAgo > 1000) {
        return `${Math.floor(timestampAgo / 1000)}초 전`
      }
    } else {
      return '-';
    }
  },
}

const style = {
  quickAttending: makeStyles((theme) => ({
    attended: {
      color: amber[800],
    },
    notAttended: {
      opacity: 0.25,
      '&:hover': {
        color: amber[600],
        opacity: 1,
      },
    },
  })),
  schedule: makeStyles((theme) => ({
    attender: {
      paddingBottom: theme.spacing(0.1),
      paddingTop: theme.spacing(0.1),
    },
    avatar: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    schedule: {
      border: '1px solid lightgray',
    },
    scheduleAppBar: {
      alignItems: 'center',
      backgroundColor: blue[400],
      color: grey['A100'],
      display: 'flex',
      flexDirection: 'row',
      padding: theme.spacing(1),
    },
    scheduleBody: {
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    scheduleHead: {
      fontWeight: 'bold',
      paddingBottom: theme.spacing(1),
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingTop: theme.spacing(1.5),
      textAlign: 'center',
    },
    scheduleHeadEmpty: {
      padding: theme.spacing(0.5),
    },
    scheduleNote: {
      borderTop: '1px solid lightgray',
      color: grey['800'],
      paddingBottom: theme.spacing(1),
      paddingTop: theme.spacing(1),
      marginTop: theme.spacing(0.5),
    },
    scheduleNoteLine: {
      margin: 0,
    },
  })),
};

export default function Schedule(props) {
  const classes = style.schedule();

  const { setNewSchedule } = props.context.getStates();

  return (
    <div className={classes.schedule}>
      <div className={classes.scheduleAppBar}>
        <b>{
          '일월화수목금토'[props.schedule.day] + ' ' +
          date.HHmm(props.schedule.secondsFrom) + ' - ' +
          date.HHmm(props.schedule.secondsTo)
        }</b>
        <span style={{flexGrow: 1}}></span>
        <IconButton color="inherit" size="small" onClick={(event) => {
          setNewSchedule(create.schedule(
            props.schedule.rowid,
            props.schedule.attendings,
            props.schedule.day,
            props.schedule.name,
            props.schedule.note,
            props.schedule.secondsFrom,
            props.schedule.secondsTo,
          ));
        }}>
          <PlaylistAddCheckOutlinedIcon fontSize="small" />
        </IconButton>
      </div>

      {props.schedule.name === '' ? (
        <div className={classes.scheduleHeadEmpty}></div>
      ) : (
        <div className={classes.scheduleHead}>
          {props.schedule.name}
        </div>
      )}

      <div className={classes.scheduleBody}>
        <table style={{width: '100%'}}>
          <tbody>
            {props.schedule.attendings.map(attending => (
              <tr key={attending.attenderRowid}>
                <td>
                  <Button className={classes.attender}
                    onClick={(event) => {
                      props.setQuickAttending((oldQuickAttending) => ({
                        ...oldQuickAttending,
                        rowid: attending.attenderRowid
                      }));
                    }}
                    startIcon={recreate.attendedToday(attending.lastAttended) ? (
                      <StyledBadge overlap="circular" variant="dot"
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                      >
                        <Avatar className={classes.avatar}
                          src={'data:image/jpeg;base64,' + attending.attenderPicture64}
                        />
                      </StyledBadge>
                    ) : (
                      <Avatar className={classes.avatar}
                        src={'data:image/jpeg;base64,' + attending.attenderPicture64}
                      />
                    )
                    }
                  >
                    {attending.attenderName}
                  </Button>
                </td>
                <td>{recreate.timeAgo(attending.lastAttended)}</td>
              </tr>
            ))}
            {props.schedule.note !== '' && (
              <tr>
                <td colSpan={3}>
                  <div className={classes.scheduleNote}>
                    {props.schedule.note.split('\n').map((line, index) => (
                      <p key={index} className={classes.scheduleNoteLine}>{line}</p>
                    ))}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}